import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Button and IconButton: shadcn/ui's Button on Radix Slot, restyled as the kit's wp-btn and wp-ibtn
// (mockups/kit/components/atoms.js, surface.css "buttons"). Theme utilities only.
//
// Variants: secondary (the kit's default hairline button), primary (cyan), quiet (the kit's ghost)
// and danger. compact is the kit's sm size, for the HUD. A glyph goes in the icon slot; the icon set
// (E09-S1-T3) fills it, and any node will do until then.
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn, focusRing, minTarget } from "./cn.js";
export const buttonVariants = cva([
    "inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap",
    "rounded-8 border font-sans font-semibold leading-none",
    "transition duration-fast ease-wp active:translate-y-px",
    "disabled:pointer-events-none disabled:opacity-45",
    minTarget,
    focusRing,
], {
    variants: {
        variant: {
            secondary: "border-wp-line-strong bg-transparent text-wp-text hover:border-wp-accent/44 hover:bg-br-sky/9",
            primary: "border-transparent bg-wp-accent text-wp-accent-ink hover:brightness-110",
            quiet: "border-transparent bg-transparent text-wp-text-2 hover:bg-br-sky/9 hover:text-wp-text",
            danger: "border-transparent bg-stop text-br-white hover:brightness-110",
        },
        compact: {
            false: "h-8 px-3 text-13",
            true: "h-7 px-2.5 text-12",
        },
    },
    defaultVariants: { variant: "secondary", compact: false },
});
export function Button({ className, variant, compact, icon, asChild = false, type = "button", children, ...props }) {
    const classes = cn(buttonVariants({ variant, compact }), className);
    if (asChild)
        return (_jsx(Slot, { "data-compact": compact ?? false, className: classes, ...props, children: children }));
    return (_jsxs("button", { type: type, "data-compact": compact ?? false, className: classes, ...props, children: [icon ? _jsx(Glyph, { children: icon }) : null, children] }));
}
export const iconButtonVariants = cva([
    "grid shrink-0 cursor-pointer place-items-center rounded-8 border-0 p-0",
    "transition duration-fast ease-wp",
    "disabled:pointer-events-none disabled:opacity-40",
    minTarget,
    focusRing,
], {
    variants: {
        variant: {
            quiet: [
                "bg-transparent text-wp-text-2 hover:bg-br-sky/9 hover:text-wp-text",
                "aria-pressed:bg-wp-accent/13 aria-pressed:text-wp-accent",
            ],
            primary: "rounded-pill bg-wp-accent text-wp-accent-ink hover:brightness-110",
            danger: "bg-transparent text-stop-soft hover:bg-stop/20",
        },
        compact: {
            false: "size-8",
            true: "size-7",
        },
    },
    defaultVariants: { variant: "quiet", compact: false },
});
export function IconButton({ className, variant, compact, label, icon, type = "button", ...props }) {
    return (_jsx("button", { type: type, "aria-label": label, "data-compact": compact ?? false, className: cn(iconButtonVariants({ variant, compact }), className), ...props, children: _jsx(Glyph, { children: icon }) }));
}
/** A decorative glyph slot: hidden from assistive tech, sized by its content. */
export function Glyph({ children, className }) {
    return (_jsx("span", { "aria-hidden": "true", className: cn("inline-grid shrink-0 place-items-center", className), children: children }));
}
//# sourceMappingURL=button.js.map